import React, { useMemo, useEffect } from 'react';
import {
    WppButton,
    WppTypography,
    WppInput,
    WppSegmentedControl,
    WppSegmentedControlItem,
    WppTooltip,
    WppIconInfo,
} from '@wppopen/components-library-react';
import { AnalyticsActionType } from '@wppopen/core';
import { useOs } from '@wppopen/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './SelectionSection.module.scss';
import { ButtonDataStatus, Localization, requestFormatter, SelectionScenarioSex } from 'utils';
import { InteractiveMap, DropdownComponent } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchScenarioPlots,
    PayloadScenarioUSProps,
    scenarioSlice,
} from 'Redux/createScenarioSlice';
import { TooltipCases } from 'pages/Projection/TooltipCases/TooltipCases';

declare global {
    interface Window {
        env: any;
    }
}

interface SelectionSectionProps {
    handlerBooleanStatement: (key: ButtonDataStatus, value: boolean) => void;
    isRunClicked: boolean;
    localization: Localization;
    testId: string;
}

const {
    Main,
    MainSelection,
    MainButtons,
    MainButtonsWrapper,
    MainSelectionItem,
    MainSelectionItemTitle,
    MainSelectionItemInput,
} = styles;

export const SelectionSection = ({
    handlerBooleanStatement,
    isRunClicked,
    localization,
    testId,
}: SelectionSectionProps) => {
    const { osApi } = useOs();
    const dispatch = useDispatch();
    const {
        [localization]: { isDataLoading, selectionData, selectionState, defaultSelectionState },
    } = useSelector(scenarioSlice);
    const validationSchema = Yup.object().shape({
        sex: Yup.string().required('Field can’t be blank'),
        age_group: Yup.array().min(1, 'Field can’t be blank'),
        heritage: Yup.array().min(1, 'Field can’t be blank'),
        category: Yup.array().min(1, 'Field can’t be blank'),
        demographic: Yup.string()
            .required('Field can’t be blank')
            .test('len', 'Pick 1-100', (val: any) => {
                if (val === undefined) {
                    return true;
                }
                return Number(val) > 0 && Number(val) <= 100;
            }),
    });

    const formik = useFormik({
        initialValues: selectionState,
        validateOnMount: false,
        validationSchema: validationSchema,
        onSubmit: async () => {
            requestHandler();
        },
    });

    const isSetDefaultButtonVisible = useMemo(() => {
        const staticState: any = { ...selectionData };
        const dynamicState: any = { ...formik.values };
        let result = true;
        for (const key in selectionData) {
            if (typeof staticState[key] === 'string') {
                result = JSON.stringify(dynamicState[key]) === JSON.stringify(staticState[key]);
            }
            if (typeof staticState[key] === 'object') {
                const staticStatePart = [...staticState[key]];
                const dynamicStatePart = [...dynamicState[key]];
                result =
                    JSON.stringify(staticStatePart.sort()) ===
                    JSON.stringify(dynamicStatePart.sort());
            }
            if (result === false) {
                break;
            }
        }

        return result;
    }, [formik.values, selectionData]);

    const { sex, age_group, heritage, category, demographic, geoMapSelection } = formik.values;

    const handleGeoMapState = (dmaData: any, regionData: any, currentSegment: any) => {
        const updatedData = {
            DMA: dmaData,
            REGION: [...regionData],
            Segment: currentSegment,
        };
        formik.setFormikState((prev: any) => {
            const result = {
                ...prev.values,
                ['geoMapSelection']: updatedData,
            };
            return {
                ...prev,
                values: result,
            };
        });
        handlerBooleanStatement(ButtonDataStatus.IS_BUTTON_RUN, false);
    };

    const handlerSelection = (key: string, value: any, isSingleSelected?: boolean) => {
        formik.setFormikState((prev: any) => {
            const result = {
                ...prev.values,
                [key]: isSingleSelected ? [value] : value,
            };
            return {
                ...prev,
                values: result,
            };
        });
        handlerBooleanStatement(ButtonDataStatus.IS_BUTTON_RUN, false);
    };

    const handlerResetDefault = () => {
        formik.setValues(defaultSelectionState);
        handlerBooleanStatement(ButtonDataStatus.IS_BUTTON_RUN, false);
    };

    const requestHandler = () => {
        handlerBooleanStatement(ButtonDataStatus.IS_DATA_LOADING, true);
        const selectionUSData: PayloadScenarioUSProps = {
            GEO_UNIT: geoMapSelection['Segment'],
            GEO_NAME: geoMapSelection[geoMapSelection['Segment']],
            plot_type: 'scenario',
            SEX: requestFormatter(sex.split(',')),
            AGE_GRP: age_group,
            ETH_GRP: heritage,
            CATEGORY: category,
            PCT: Number(demographic),
        };
        dispatch(
            fetchScenarioPlots({
                localization,
                payload: selectionUSData,
                selection: {
                    ...formik.values,
                },
            }),
        );
    };

    useEffect(() => {
        osApi.analytics.track({
            type: AnalyticsActionType.page,
            payload: 'Alternative future scenarios',
        });
    });

    return (
        <div className={Main} data-testid={testId}>
            <form
                id="selection"
                className={MainSelection}
                onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                }}
            >
                <div className={MainSelectionItem} data-testid={`${testId}.SEX`}>
                    <div className={MainSelectionItemTitle}>
                        <WppTypography type="s-strong" data-testid={`${testId}.SEX.TITLE`}>
                            Sex
                        </WppTypography>
                    </div>
                    <WppSegmentedControl
                        onWppChange={(e) => handlerSelection('sex', e.detail.value)}
                        value={sex}
                        size="s"
                        variant="text"
                        id="sex"
                        data-testid={`${testId}.SEX.SEGMENT`}
                    >
                        <WppSegmentedControlItem
                            value={`${SelectionScenarioSex.FEMALES},${SelectionScenarioSex.MALES}`}
                            itemID="F,M"
                            key="F,M"
                            disabled={isDataLoading}
                            data-testid={`${testId}.SEX.SEGMENT.ALL`}
                        >
                            All
                        </WppSegmentedControlItem>
                        <WppSegmentedControlItem
                            value={`${SelectionScenarioSex.FEMALES}`}
                            itemID="F"
                            key="F"
                            disabled={isDataLoading}
                            data-testid={`${testId}.SEX.SEGMENT.FEMALE`}
                        >
                            F
                        </WppSegmentedControlItem>
                        <WppSegmentedControlItem
                            value={`${SelectionScenarioSex.MALES}`}
                            itemID="M"
                            key="M"
                            disabled={isDataLoading}
                            data-testid={`${testId}.SEX.SEGMENT.MALE`}
                        >
                            M
                        </WppSegmentedControlItem>
                    </WppSegmentedControl>
                </div>
                <div className={MainSelectionItem} data-testid={`${testId}.AGE`}>
                    <div className={MainSelectionItemTitle}>
                        <WppTypography type="s-strong" data-testid={`${testId}.AGE.TITLE`}>
                            Age group
                        </WppTypography>
                    </div>
                    <DropdownComponent
                        message={
                            formik.touched.age_group && formik.errors.age_group
                                ? formik.errors.age_group
                                : ''
                        }
                        messageType={
                            formik.touched.age_group && formik.errors.age_group
                                ? 'error'
                                : undefined
                        }
                        value={age_group}
                        handleChange={handlerSelection}
                        keyProp="age_group"
                        placeholder="Select age group"
                        inputDisabled={isDataLoading}
                        minItemSelected={1}
                        selectionData={selectionData}
                        wppSize="s"
                        dataTestId={`${testId}.AGE.SECTION`}
                    />
                </div>
                <div className={MainSelectionItem} data-testid={`${testId}.ETHNIC`}>
                    <div className={MainSelectionItemTitle}>
                        <WppTypography type="s-strong" data-testid={`${testId}.ETHNIC.TITLE`}>
                            Ethnic group
                        </WppTypography>
                    </div>
                    <DropdownComponent
                        message={
                            formik.touched.heritage && formik.errors.heritage
                                ? formik.errors.heritage
                                : ''
                        }
                        messageType={
                            formik.touched.heritage && formik.errors.heritage ? 'error' : undefined
                        }
                        value={heritage}
                        handleChange={handlerSelection}
                        keyProp="heritage"
                        placeholder="Select heritage"
                        inputDisabled={isDataLoading}
                        selectionData={selectionData}
                        minItemSelected={1}
                        wppSize="s"
                        dataTestId={`${testId}.ETHNIC.SECTION`}
                    />
                </div>
                <div className={MainSelectionItem} data-testid={`${testId}.CATEGORY`}>
                    <div className={MainSelectionItemTitle}>
                        <WppTypography type="s-strong" data-testid={`${testId}.CATEGORY.TITLE`}>
                            Category
                        </WppTypography>
                    </div>
                    <DropdownComponent
                        message={
                            formik.touched.category && formik.errors.category
                                ? formik.errors.category
                                : ''
                        }
                        messageType={
                            formik.touched.category && formik.errors.category ? 'error' : undefined
                        }
                        value={category[0]}
                        handleChange={handlerSelection}
                        keyProp="category"
                        placeholder="Select category"
                        inputDisabled={isDataLoading}
                        selectionData={selectionData}
                        minItemSelected={1}
                        maxItemSelected={3}
                        isSingleSelection={true}
                        wppSize="s"
                        dataTestId={`${testId}.CATEGORY.SECTION`}
                    />
                </div>
                <div className={MainSelectionItem} data-testid={`${testId}.MAP`}>
                    <div className={MainSelectionItemTitle}>
                        <WppTypography type="s-strong" data-testid={`${testId}.MAP.TITLE`}>
                            Geo Selection
                        </WppTypography>
                    </div>
                    <InteractiveMap
                        disabled={isDataLoading}
                        handleData={handleGeoMapState}
                        currentSelection={geoMapSelection}
                        withApplyButton={false}
                        wppSize="s"
                        dataTestId={`${testId}.MAP.SECTION`}
                    />
                </div>
                <div className={MainSelectionItem} data-testid={`${testId}.ENGAGEMENT`}>
                    <div className={MainSelectionItemTitle}>
                        <WppTypography type="s-strong" data-testid={`${testId}.ENGAGEMENT.TITLE`}>
                            Engagement (%)
                        </WppTypography>
                        <WppTooltip text={TooltipCases[localization].Engagement}>
                            <WppIconInfo width={17} height={17} />
                        </WppTooltip>
                    </div>
                    <WppInput
                        value={demographic}
                        name="demographic"
                        className={MainSelectionItemInput}
                        size="s"
                        required
                        onWppChange={(e) => handlerSelection('demographic', e.detail.value)}
                        message={
                            formik.touched.demographic && formik.errors.demographic
                                ? (formik.errors.demographic as string)
                                : ''
                        }
                        messageType={
                            formik.touched.demographic && formik.errors.demographic
                                ? 'error'
                                : undefined
                        }
                        disabled={isDataLoading}
                        data-testid={`${testId}.ENGAGEMENT.SECTION.INPUT`}
                    />
                </div>
            </form>
            <div className={MainButtonsWrapper} data-testid={`${testId}.ACTIONS`}>
                <WppTypography
                    style={{ visibility: isRunClicked ? 'hidden' : 'visible' }}
                    type="xs-body"
                    data-testid={`${testId}.ACTIONS.TITLE`}
                >
                    Run to see your changes
                </WppTypography>
                <div className={MainButtons}>
                    <WppButton
                        style={{
                            visibility: isSetDefaultButtonVisible ? 'hidden' : 'visible',
                        }}
                        variant="secondary"
                        id="cancelButton"
                        onClick={handlerResetDefault}
                        disabled={isDataLoading}
                        data-testid={`${testId}.ACTIONS.RESET.BUTTON`}
                    >
                        Reset default
                    </WppButton>
                    <WppButton
                        disabled={isRunClicked || isDataLoading}
                        type="submit"
                        form="selection"
                        id="runScenarioButton"
                        data-testid={`${testId}.ACTIONS.RUN.BUTTON`}
                    >
                        Run
                    </WppButton>
                </div>
            </div>
        </div>
    );
};
